/*
  Element theme runtime.
  Cart helpers, quantity controls, and overlay modals.
  Element storefront runtime.
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl'
};

theme.supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function () {
      theme.supportsPassive = true;
    }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) { }

theme.DOMready = function (callback) {
  if (document.readyState !== 'loading') callback();
  else document.addEventListener('DOMContentLoaded', callback);
};

theme.inView = function (element, callback, options = {}) {
  if (!element || typeof callback !== 'function') return () => {};

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const cleanup = callback(entry);
      if (typeof cleanup !== 'function') observer.unobserve(entry.target);
    });
  }, { rootMargin: options.margin || '0px' });

  observer.observe(element);
  return () => observer.disconnect();
};

theme.a11y = {
  trapFocusHandlers: {},

  getFocusableElements: (container) => {
    return Array.from(
      container.querySelectorAll(
        'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
      )
    );
  },

  trapFocus: (container, elementToFocus = container) => {
    const elements = theme.a11y.getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    theme.a11y.removeTrapFocus();

    theme.a11y.trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      document.addEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
    };

    theme.a11y.trapFocusHandlers.focusout = function () {
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
    };

    theme.a11y.trapFocusHandlers.keydown = function (event) {
      if (event.code.toUpperCase() !== 'TAB') return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
    document.addEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
    elementToFocus.focus();

    if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  },

  removeTrapFocus: (elementToFocus = null) => {
    document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
    document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
    document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
    if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
  }
};

theme.utils = {
  throttle: (callback) => {
    let requestId = null;
    let lastArgs;
    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };
    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };
    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };
    return throttled;
  },

  debounce: (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  waitForEvent: (element, eventName) => {
    return new Promise((resolve) => {
      const done = (event) => {
        if (event.target === element) {
          element.removeEventListener(eventName, done);
          resolve(event);
        }
      };
      element.addEventListener(eventName, done);
    });
  },

  fetchConfig: (type = 'json', method = 'POST') => {
    return {
      method: method,
      headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
    };
  },

  sectionId: (element) => {
    if (element.hasAttribute('data-section-id')) return element.getAttribute('data-section-id');
    element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
    return element.id.replace('shopify-section-', '');
  },

  imageLoaded: (imageOrArray) => {
    if (!imageOrArray) return Promise.resolve();
    imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
    return Promise.all(imageOrArray.map((image) => {
      return new Promise((resolve) => {
        if ((image.tagName === 'IMG' && image.complete) || !image.offsetParent) {
          resolve();
        } else {
          image.onload = () => resolve();
        }
      });
    }));
  },

  externalLinksNewTab: () => {
    if (!theme.settings.externalLinksNewTab) return;
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
        link.target = '_blank';
      }
    });
  }
};

theme.pubsub = {
  PUB_SUB_EVENTS: {
    cartUpdate: 'cartUpdate',
    quantityUpdate: 'quantityUpdate',
    variantChange: 'variantChange',
    cartError: 'cartError',
    discountUpdate: 'discountUpdate',
    quantityRules: 'quantityRules',
    quantityBoundries: 'quantityBoundries'
  },
  subscribers: {},
  subscribe: (eventName, callback) => {
    if (theme.pubsub.subscribers[eventName] === undefined) {
      theme.pubsub.subscribers[eventName] = [];
    }
    theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback];
    return function unsubscribe() {
      theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => cb !== callback);
    };
  },
  publish: (eventName, data) => {
    if (!theme.pubsub.subscribers[eventName]) return;
    theme.pubsub.subscribers[eventName].forEach((callback) => callback(data));
  }
};

theme.isStorageSupported = function (type) {
  if (window.self !== window.top) return false;
  const storage = type === 'local' ? window.localStorage : window.sessionStorage;
  try {
    storage.setItem('test', '1');
    storage.removeItem('test');
    return true;
  } catch (error) {
    return false;
  }
};

theme.config.hasSessionStorage = theme.isStorageSupported('session');
theme.config.hasLocalStorage = theme.isStorageSupported('local');

{
  const revealEvents = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
  const reveal = () => {
    revealEvents.forEach((type) => window.removeEventListener(type, reveal));
    const run = () => document.body.removeAttribute('data-page-rendering');
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1000 });
    } else {
      setTimeout(run, 0);
    }
  };
  revealEvents.forEach((type) => window.addEventListener(type, reveal, { passive: true }));
}

theme.windowWidth = window.innerWidth;
window.addEventListener('resize', () => {
  const { innerWidth } = window;
  if (innerWidth !== theme.windowWidth) {
    theme.windowWidth = innerWidth;
    document.dispatchEvent(new CustomEvent('window:resize'));
  }
});

const mql = window.matchMedia(theme.config.mediaQuerySmall);
theme.config.mqlSmall = mql.matches;
mql.onchange = (query) => {
  theme.config.mqlSmall = query.matches;
  document.dispatchEvent(new CustomEvent(query.matches ? 'matchSmall' : 'unmatchSmall'));
};

theme.cookiesEnabled = function () {
  try {
    document.cookie = 'elementcookietest=1';
    const enabled = document.cookie.indexOf('elementcookietest=') !== -1;
    document.cookie = 'elementcookietest=1; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
    return enabled;
  } catch (error) {
    return false;
  }
};

theme.initWhenVisible = class {
  constructor(callback, delay = 5000) {
    if (typeof callback !== 'function') return;

    let done = false;
    const events = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
    const run = () => {
      if (done) return;
      done = true;
      events.forEach((type) => window.removeEventListener(type, run));
      callback();
    };

    events.forEach((type) => window.addEventListener(type, run, { passive: true }));
    setTimeout(run, delay);
  }
};

theme.DOMready(theme.utils.externalLinksNewTab);

window.addEventListener('DOMContentLoaded', () => {
  document.body.classList.add('loaded');
  document.dispatchEvent(new CustomEvent('page:loaded'));
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) document.body.classList.remove('unloading');
});
window.addEventListener('beforeunload', () => {
  document.body.classList.add('unloading');
});

window.BaseElementMixin = window.BaseElementMixin || ((Base) => class extends Base {
  connectedCallback() {
    this.eventsController ??= new AbortController();
  }

  disconnectedCallback() {
    this.eventsController?.abort();
    this.eventsController = null;
    if (this._cleanups) {
      while (this._cleanups.length) {
        try {
          this._cleanups.pop()();
        } catch (error) {
          console.error('[BaseElement] cleanup failed', error);
        }
      }
    }
  }

  on(target, type, handler, options = {}) {
    this.eventsController ??= new AbortController();
    target.addEventListener(type, handler, { ...options, signal: this.eventsController.signal });
    return handler;
  }

  registerCleanup(teardown) {
    if (typeof teardown === 'function') (this._cleanups ??= []).push(teardown);
    return teardown;
  }
});

class BaseElement extends window.BaseElementMixin(HTMLElement) { }

class ElementDialog extends BaseElement {
  constructor() {
    super();
    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow'
    };
    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    };
  }

  static get observedAttributes() {
    return ['open'];
  }

  get shouldLock() {
    return this.hasAttribute('data-lock') || this.classList.contains('drawer');
  }

  get shouldAppendToBody() {
    return this.hasAttribute('data-append-body');
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return this.querySelector('.fixed-modal, .overlay');
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('[data-dialog-focus], .drawer__close, button, a, input, textarea, select');
  }

  connectedCallback() {
    super.connectedCallback();
    this.controls.forEach((button) => this.on(button, 'click', this.onButtonClick.bind(this)));
    this.on(document, 'keyup', (event) => {
      if (event.code === 'Escape' && this.open) this.hide();
    });

    if (window.Shopify?.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        this.on(section, 'shopify:section:select', (event) => this.show(null, !event.detail.load));
        this.on(section, 'shopify:section:deselect', this.hide.bind(this));
      }
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'open') return;

    this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

    if (oldValue === null && newValue !== null) {
      this.hidden = false;
      this.removeAttribute('inert');
      this.originalParentBeforeAppend = null;
      if (this.shouldAppendToBody && this.parentElement !== document.body) {
        this.originalParentBeforeAppend = this.parentElement;
        document.body.append(this);
      }
      this.playShow(newValue !== 'immediate').then(() => {
        this.afterShow();
        this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }));
      });
    } else if (oldValue !== null && newValue === null) {
      this.setAttribute('inert', '');
      this.playHide().then(() => {
        this.afterHide();
        if (!this.hasAttribute('inert')) return;
        if (this.parentElement === document.body && this.originalParentBeforeAppend) {
          this.originalParentBeforeAppend.appendChild(this);
          this.originalParentBeforeAppend = null;
        }
        this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }));
        this.hidden = true;
      });
    }

    this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));
  }

  onButtonClick(event) {
    event.preventDefault();
    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;
    this.removeAttribute('open');
    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  show(activeElement = null, animate = true) {
    if (this.open) return;
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');
    if (this.shouldLock) document.body.classList.add(this.classes.opening);
    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      if (!this.shouldLock) return;
      lockLayerCount.value = Math.max(0, lockLayerCount.value - 1);
      const cartOpen = document.querySelector('cart-drawer.is-open, .element-cart.is-open');
      document.body.classList.toggle(this.classes.open, lockLayerCount.value > 0 || Boolean(cartOpen));
    });
  }

  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    if (!this.shouldLock) return;
    lockLayerCount.value += 1;
    document.body.classList.remove(this.classes.opening);
    document.body.classList.add(this.classes.open);
  }

  playShow(animate) {
    const activate = () => this.setAttribute('active', '');
    if (animate) setTimeout(activate, 75);
    else activate();
    return this.waitForOverlay();
  }

  playHide() {
    this.removeAttribute('active');
    return this.waitForOverlay();
  }

  waitForOverlay() {
    return new Promise((resolve) => {
      const overlay = this.overlay;
      if (!overlay) {
        resolve();
        return;
      }
      const computedStyle = window.getComputedStyle(overlay);
      const duration = parseFloat(computedStyle.transitionDuration);
      const hasTransition = computedStyle.transitionProperty !== 'none' && duration > 0;
      if (!hasTransition) {
        resolve();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      overlay.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, Math.max(700, duration * 1000 + 80));
    });
  }
}

const lockLayerCount = { value: 0 };
customElements.define('element-dialog', ElementDialog);
window.ElementDialog = ElementDialog;

class QuantityInput extends BaseElement {
  get sectionId() { return this.getAttribute('data-section-id'); }
  get productId() { return this.getAttribute('data-product-id'); }
  get input() { return this.querySelector('input'); }
  get value() { return this.input.value; }

  connectedCallback() {
    super.connectedCallback();
    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    this.on(this.input, 'change', this.onInputChange.bind(this));
    this.on(this.input, 'focus', () => setTimeout(() => this.input.select()));
    this.buttons.forEach((button) => this.on(button, 'click', this.onButtonClick.bind(this)));
    this.validateQtyRules();
    this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this)));
    this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this)));
    this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this)));
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.input.disabled) return;
    const previousValue = this.input.value;
    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === '') this.input.value = parseInt(this.input.min);
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      buttonMinus?.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      buttonPlus?.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;
    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (const selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;
      if (selector === '.quantity__input') {
        for (const attribute of ['data-cart-quantity', 'data-min', 'data-max', 'step']) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          else current.removeAttribute(attribute);
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;
    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    };
    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
    this.input.min = min;
    if (max) this.input.max = max;
    else this.input.removeAttribute('max');
    this.input.value = min;
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('quantity-input', QuantityInput);

class CartCount extends BaseElement {
  connectedCallback() {
    super.connectedCallback();
    this.registerCleanup(theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)));
  }
  get itemCount() { return parseInt(this.innerText); }
  onCartUpdate(event) {
    if (event.cart.errors) return;
    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('cart-count', CartCount);
